import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  MeetingAnnouncementChatView,
  MeetingAnnouncementChatInput,
  MeetingAnnouncementChatUpdateInput,
  ApiError,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '../types';
import { useAuth } from '@/contexts/AuthContext';

export const meetingChatKeys = {
  all: ['meetingChats'] as const,
  lists: () => [...meetingChatKeys.all, 'list'] as const,
  forAnnouncement: (announcementId: string) =>
    [...meetingChatKeys.lists(), 'announcement', announcementId] as const,
  mine: () => [...meetingChatKeys.lists(), 'mine'] as const,
  details: () => [...meetingChatKeys.all, 'detail'] as const,
  detail: (id: string) => [...meetingChatKeys.details(), id] as const,
};

export const useAnnouncementChats = (
  announcementId: string,
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementChatView[], ApiError>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<MeetingAnnouncementChatView[], ApiError>({
    queryKey: meetingChatKeys.forAnnouncement(announcementId),
    queryFn: async () => {
      const response = await apiClient.get<ApiPlatformCollectionResponse<MeetingAnnouncementChatView>>(
        `/meeting-announcements/${announcementId}/chats`
      );
      // Handle null response from API
      if (!response.data) {
        return [];
      }
      return extractCollectionItems(response.data);
    },
    enabled: !!announcementId,
    ...options,
  });
};

export const useMyChats = (
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementChatView[], ApiError>,
    'queryKey' | 'queryFn'
  >
) => {
  const { isAuthenticated } = useAuth();
  
  return useQuery<MeetingAnnouncementChatView[], ApiError>({
    queryKey: meetingChatKeys.mine(),
    queryFn: async () => {
      const response = await apiClient.get<ApiPlatformCollectionResponse<MeetingAnnouncementChatView>>(
        '/meeting-chats/me'
      );
      return extractCollectionItems(response.data);
    },
    enabled: isAuthenticated && (options?.enabled !== false),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    ...options,
  });
};

export const useChat = (
  chatId: string,
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementChatView, ApiError>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<MeetingAnnouncementChatView, ApiError>({
    queryKey: meetingChatKeys.detail(chatId),
    queryFn: async () => {
      const response = await apiClient.get<MeetingAnnouncementChatView>(
        `/meeting-chats/${chatId}`
      );
      return response.data;
    },
    enabled: !!chatId,
    ...options,
  });
};

export const useCreateChat = (
  options?: UseMutationOptions<
    MeetingAnnouncementChatView,
    ApiError,
    { announcementId: string; data: MeetingAnnouncementChatInput }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementChatView,
    ApiError,
    { announcementId: string; data: MeetingAnnouncementChatInput }
  >({
    mutationFn: async ({ announcementId, data }) => {
      const response = await apiClient.post<MeetingAnnouncementChatView>(
        `/meeting-announcements/${announcementId}/chats`,
        data
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: meetingChatKeys.forAnnouncement(variables.announcementId),
      });
      queryClient.invalidateQueries({
        queryKey: meetingChatKeys.mine(),
      });
    },
    ...options,
  });
};

export const useUpdateChat = (
  options?: UseMutationOptions<
    MeetingAnnouncementChatView,
    ApiError,
    { chatId: string; data: MeetingAnnouncementChatUpdateInput }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementChatView,
    ApiError,
    { chatId: string; data: MeetingAnnouncementChatUpdateInput }
  >({
    mutationFn: async ({ chatId, data }) => {
      const response = await apiClient.patch<MeetingAnnouncementChatView>(
        `/meeting-chats/${chatId}`,
        data
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(meetingChatKeys.detail(variables.chatId), data);
      queryClient.invalidateQueries({
        queryKey: meetingChatKeys.lists(),
      });
    },
    ...options,
  });
};

export const useBanChat = (
  options?: UseMutationOptions<
    MeetingAnnouncementChatView,
    ApiError,
    { chatId: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementChatView,
    ApiError,
    { chatId: string }
  >({
    mutationFn: async ({ chatId }) => {
      const response = await apiClient.patch<MeetingAnnouncementChatView>(
        `/meeting-chats/${chatId}/ban`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(meetingChatKeys.detail(variables.chatId), data);
      queryClient.invalidateQueries({
        queryKey: meetingChatKeys.lists(),
      });
    },
    ...options,
  });
};
